import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { getErrorMessage } from '@/utils/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { ClipboardCheck, Save, ArrowLeft, Brain, ChevronDown, ChevronUp, CheckCircle, Star, Building2 } from 'lucide-react';
import { TokenAwardPrompt } from '@/components/TokenBoard';

const INTERVENTION_QUESTIONS = [
  { key: 'q1', label: "Child's response to verbal instructions" },
  { key: 'q2', label: "Effectiveness of visual supports used" },
  { key: 'q3', label: "Child's ability to manage transitions" },
  { key: 'q4', label: "Appropriateness of sensory environment" },
  { key: 'q5', label: "Effectiveness of reinforcement strategies" },
  { key: 'q6', label: "Child's engagement with instructor" },
  { key: 'q7', label: "Child's emotional regulation" },
  { key: 'q8', label: "Session pacing effectiveness" },
];

const CRITICAL_THINKING_POINTS = [
  { key: 'p1', label: "Observes and notices details about objects/environment" },
  { key: 'p2', label: "Makes predictions about what might happen" },
  { key: 'p3', label: "Shows cause-and-effect understanding" },
  { key: 'p4', label: "Attempts independent problem-solving" },
  { key: 'p5', label: "Communicates reasoning (verbal or non-verbal)" },
];

const getInterventionLevel = (score) => {
  if (score >= 32) return { level: 'Minimal Support', color: 'text-green-600 bg-green-50 border-green-200', desc: 'Child demonstrates strong skills. Maintain current approach.' };
  if (score >= 24) return { level: 'Moderate Support', color: 'text-amber-600 bg-amber-50 border-amber-200', desc: 'Some areas need attention. Consider targeted adjustments.' };
  if (score >= 16) return { level: 'Significant Support', color: 'text-orange-600 bg-orange-50 border-orange-200', desc: 'Multiple areas need improvement. Review teaching strategies.' };
  return { level: 'Intensive Support', color: 'text-red-600 bg-red-50 border-red-200', desc: 'Comprehensive review of environment and approach needed.' };
};

const getCriticalLevel = (score) => {
  if (score === 5) return { level: 'Advanced', color: 'text-green-600' };
  if (score === 4) return { level: 'Proficient', color: 'text-blue-600' };
  if (score === 3) return { level: 'Developing', color: 'text-amber-600' };
  if (score === 2) return { level: 'Emerging', color: 'text-orange-600' };
  return { level: 'Needs Support', color: 'text-red-600' };
};

const LEVEL_COLORS = ['bg-red-50 border-red-200 text-red-700', 'bg-orange-50 border-orange-200 text-orange-700', 'bg-amber-50 border-amber-200 text-amber-700', 'bg-blue-50 border-blue-200 text-blue-700', 'bg-green-50 border-green-200 text-green-700'];

export default function AssessmentPage() {
  const { sessionId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [criteria, setCriteria] = useState([]);
  const [criteriaRatings, setCriteriaRatings] = useState({});
  const [scores, setScores] = useState(Object.fromEntries(INTERVENTION_QUESTIONS.map(q => [q.key, 3])));
  const [checks, setChecks] = useState(Object.fromEntries(CRITICAL_THINKING_POINTS.map(p => [p.key, false])));
  const [criticalNotObserved, setCriticalNotObserved] = useState(false);
  const [showCriteria, setShowCriteria] = useState(true);
  const [showIntervention, setShowIntervention] = useState(true);
  const [showCritical, setShowCritical] = useState(true);
  const [saving, setSaving] = useState(false);
  const [parentInWater, setParentInWater] = useState(false);
  const [facilities, setFacilities] = useState([]);
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [showTokenAward, setShowTokenAward] = useState(false); // null = not chosen, 'private' = private pool, facility_id = specific facility

  useEffect(() => {
    const fetchData = async () => {
      try {
        const sessRes = await api.get(`/sessions/${sessionId}`);
        setSession(sessRes.data);
        const critRes = await api.get(`/criteria?child_id=${sessRes.data.child_id}`);
        setCriteria(critRes.data);
        const facRes = await api.get('/facility/list');
        setFacilities(facRes.data);
        // Default to first facility if only one exists
        if (facRes.data.length === 1) setSelectedFacility(facRes.data[0].facility_id);
      } catch { toast.error('Failed to load session'); }
    };
    fetchData();
  }, [sessionId]);

  const interventionTotal = Object.values(scores).reduce((a, b) => a + b, 0);
  const criticalTotal = criticalNotObserved ? 0 : Object.values(checks).filter(Boolean).length;
  const interventionLevel = getInterventionLevel(interventionTotal);
  const criticalLevel = getCriticalLevel(criticalTotal);
  const allCriteriaRated = criteria.length > 0 && criteria.every(c => criteriaRatings[c.criteria_id] !== undefined);
  const criticalThinkingAddressed = criticalNotObserved || Object.values(checks).some(Boolean);

  const handleSubmit = async () => {
    if (!allCriteriaRated && criteria.length > 0) {
      toast.error('Please rate all co-created criteria before submitting.');
      setShowCriteria(true);
      return;
    }
    if (!criticalThinkingAddressed) {
      toast.error('Please complete the Critical Thinking section — either check observed skills or select "Did not observe critical thinking".');
      setShowCritical(true);
      return;
    }
    setSaving(true);
    try {
      // Save criteria ratings first
      for (const crit of criteria) {
        const level = criteriaRatings[crit.criteria_id];
        if (level !== undefined) {
          await api.post('/criteria-ratings', {
            criteria_id: crit.criteria_id,
            session_id: sessionId,
            child_id: session.child_id,
            level
          });
        }
      }

      // Save assessment
      const criticalChecks = criticalNotObserved
        ? Object.fromEntries(CRITICAL_THINKING_POINTS.map(p => [p.key, false]))
        : checks;
      await api.post('/assessments', {
        session_id: sessionId,
        child_id: session.child_id,
        intervention_scores: scores,
        critical_thinking_checks: criticalChecks,
        critical_thinking_not_observed: criticalNotObserved,
        parent_in_water: parentInWater,
        facility_id: selectedFacility || null,
      });
      toast.success('Assessment saved!');
      // Check if token program exists before showing award screen
      try {
        const tkRes = await api.get(`/token-economy/${session.child_id}`);
        if (tkRes.data?.enabled) {
          setShowTokenAward(true);
          return;
        }
      } catch { /* no program — skip to dashboard */ }
      navigate('/dashboard');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to save assessment'));
    } finally { setSaving(false); }
  };

  if (!session) return <div className="flex items-center justify-center p-12"><img src="/logo.png" alt="Loading" className="w-12 h-12 animate-pulse" /></div>;

  // Show token award prompt after assessment submission
  if (showTokenAward) {
    return (
      <div className="max-w-2xl mx-auto p-6 animate-fadeIn" data-testid="token-award-screen">
        <TokenAwardPrompt
          childId={session.child_id}
          sessionId={sessionId}
          sessionNumber={session.session_number}
          onComplete={() => navigate('/dashboard')}
        />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto animate-fadeIn" data-testid="assessment-page">
      <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4 rounded-xl gap-2 text-slate-500">
        <ArrowLeft className="w-4 h-4" /> Back
      </Button>

      <div className="mb-6">
        <h1 className="text-3xl font-extrabold text-[#1E1B4B]" style={{ fontFamily: 'Nunito' }}>Session Assessment</h1>
        <p className="text-slate-500">Session #{session.session_number} - {new Date(session.date).toLocaleDateString()}</p>
      </div>

      {/* SECTION 1: Co-Created Assessment Criteria (MAIN FOCUS) */}
      <Card className="rounded-3xl border-[#22D3EE]/30 shadow-md mb-6 ring-2 ring-[#22D3EE]/20" data-testid="criteria-rating-section">
        <CardHeader className="cursor-pointer" onClick={() => setShowCriteria(!showCriteria)}>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Nunito' }}>
              <Star className="w-5 h-5 text-[#C4F017]" /> Co-Created Assessment Criteria
              <span className="text-xs font-normal bg-[#C4F017]/20 text-lime-700 px-2 py-0.5 rounded-full">REQUIRED</span>
            </CardTitle>
            {showCriteria ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
          </div>
          {!allCriteriaRated && criteria.length > 0 && (
            <p className="text-sm text-amber-600 font-medium mt-1">Rate each criteria to continue</p>
          )}
        </CardHeader>
        {showCriteria && (
          <CardContent className="space-y-5">
            {criteria.length === 0 ? (
              <div className="text-center py-6 bg-amber-50 rounded-2xl">
                <p className="text-amber-700 font-semibold">No criteria created yet</p>
                <p className="text-sm text-amber-600 mt-1">Go to Assessment Criteria page to create criteria first.</p>
              </div>
            ) : (
              criteria.map(crit => (
                <div key={crit.criteria_id} className="bg-slate-50 rounded-2xl p-4" data-testid={`criteria-rate-${crit.criteria_id}`}>
                  <h4 className="font-bold text-[#1E1B4B] mb-1" style={{ fontFamily: 'Nunito' }}>{crit.title}</h4>
                  {crit.description && <p className="text-sm text-slate-500 mb-3">{crit.description}</p>}
                  <div className="space-y-2">
                    {(crit.levels || []).map((lvl, idx) => (
                      <button
                        key={lvl.level}
                        onClick={() => setCriteriaRatings(prev => ({ ...prev, [crit.criteria_id]: lvl.level }))}
                        className={`w-full flex items-center gap-3 rounded-xl p-3 border-2 transition-all text-left ${
                          criteriaRatings[crit.criteria_id] === lvl.level
                            ? `${LEVEL_COLORS[idx]} ring-2 ring-offset-1 ring-current shadow-md scale-[1.01]`
                            : 'border-slate-200 bg-white hover:border-slate-300'
                        }`}
                        data-testid={`rate-${crit.criteria_id}-level-${lvl.level}`}
                      >
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-extrabold text-sm flex-shrink-0 ${
                          criteriaRatings[crit.criteria_id] === lvl.level ? 'bg-white shadow-sm' : 'bg-slate-100'
                        }`} style={{ fontFamily: 'Nunito' }}>
                          {lvl.level}
                        </div>
                        <p className="text-sm font-medium flex-1">{lvl.description || `Level ${lvl.level}`}</p>
                        {criteriaRatings[crit.criteria_id] === lvl.level && (
                          <CheckCircle className="w-5 h-5 text-current flex-shrink-0" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              ))
            )}
          </CardContent>
        )}
      </Card>

      {/* SECTION 2: Intervention Evaluation */}
      <Card className="rounded-3xl border-slate-100 shadow-sm mb-6" data-testid="intervention-section">
        <CardHeader className="cursor-pointer" onClick={() => setShowIntervention(!showIntervention)}>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Nunito' }}>
              <ClipboardCheck className="w-5 h-5 text-[#22D3EE]" /> Intervention Evaluation
              <span className="text-sm font-normal text-slate-400">({interventionTotal}/40)</span>
            </CardTitle>
            {showIntervention ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
          </div>
        </CardHeader>
        {showIntervention && (
          <CardContent className="space-y-4">
            <p className="text-sm text-slate-500">Rate each area on a scale of 1 (poor) to 5 (excellent)</p>
            {INTERVENTION_QUESTIONS.map(q => (
              <div key={q.key} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 bg-slate-50 rounded-2xl p-4" data-testid={`question-${q.key}`}>
                <p className="flex-1 text-sm font-medium text-slate-700">{q.label}</p>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map(val => (
                    <button
                      key={val}
                      onClick={() => setScores(prev => ({ ...prev, [q.key]: val }))}
                      className={`w-10 h-10 rounded-xl font-bold text-sm transition-all ${
                        scores[q.key] === val
                          ? 'bg-[#22D3EE] text-white shadow-md scale-110'
                          : scores[q.key] > val
                          ? 'bg-[#A5F3FC] text-[#0E7490]'
                          : 'bg-white text-slate-400 border border-slate-200'
                      }`}
                      data-testid={`score-${q.key}-${val}`}
                    >
                      {val}
                    </button>
                  ))}
                </div>
              </div>
            ))}
            <div className={`rounded-2xl p-4 border ${interventionLevel.color}`}>
              <p className="font-bold">Total: {interventionTotal}/40 - {interventionLevel.level}</p>
              <p className="text-sm mt-1">{interventionLevel.desc}</p>
            </div>
          </CardContent>
        )}
      </Card>

      {/* SECTION 3: Critical Thinking */}
      <Card className="rounded-3xl border-slate-100 shadow-sm mb-6" data-testid="critical-thinking-section">
        <CardHeader className="cursor-pointer" onClick={() => setShowCritical(!showCritical)}>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Nunito' }}>
              <Brain className="w-5 h-5 text-purple-500" /> Critical Thinking Log
              <span className="text-sm font-normal text-slate-400">({criticalTotal}/5)</span>
            </CardTitle>
            {showCritical ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
          </div>
        </CardHeader>
        {showCritical && (
          <CardContent className="space-y-3">
            <p className="text-sm text-slate-500">Check each thinking skill observed during the session, or indicate if critical thinking was not observed.</p>
            {/* Did Not Observe toggle */}
            <button
              type="button"
              onClick={() => {
                setCriticalNotObserved(prev => !prev);
                if (!criticalNotObserved) setChecks(Object.fromEntries(CRITICAL_THINKING_POINTS.map(p => [p.key, false])));
              }}
              className={`w-full flex items-center gap-3 rounded-2xl p-4 border-2 transition-all text-left ${
                criticalNotObserved
                  ? 'border-slate-500 bg-slate-100 ring-2 ring-slate-300'
                  : 'border-slate-200 bg-white hover:border-slate-300'
              }`}
              data-testid="critical-not-observed-toggle"
            >
              <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                criticalNotObserved ? 'bg-slate-600 border-slate-600 text-white' : 'border-slate-300 bg-white'
              }`}>
                {criticalNotObserved && <CheckCircle className="w-4 h-4" />}
              </div>
              <span className="text-sm font-semibold text-slate-700">Did not observe critical thinking this session</span>
            </button>
            {!criticalNotObserved && CRITICAL_THINKING_POINTS.map(p => (
              <div
                key={p.key}
                className="flex items-center gap-3 bg-slate-50 rounded-2xl p-4 cursor-pointer hover:bg-slate-100 transition-colors"
                data-testid={`check-${p.key}`}
                onClick={() => setChecks(prev => ({ ...prev, [p.key]: !prev[p.key] }))}
              >
                <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                  checks[p.key] ? 'bg-purple-500 border-purple-500 text-white' : 'border-slate-300 bg-white'
                }`}>
                  {checks[p.key] && <CheckCircle className="w-4 h-4" />}
                </div>
                <span className="text-sm font-medium text-slate-700 flex-1">{p.label}</span>
              </div>
            ))}
            <div className="rounded-2xl p-3 bg-purple-50 border border-purple-200">
              <p className={`font-bold ${criticalNotObserved ? 'text-slate-500' : criticalLevel.color}`}>
                {criticalNotObserved ? 'Not observed this session' : `Total: ${criticalTotal}/5 - ${criticalLevel.level}`}
              </p>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Facility & Parent in Water */}
      <Card className="rounded-3xl border-slate-100 shadow-sm mb-6" data-testid="facility-section">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-2 mb-2">
            <Building2 className="w-5 h-5 text-[#0E7490]" />
            <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider">Session Location</h3>
          </div>

          {/* Facility Selection */}
          <div className="space-y-2">
            {facilities.map(f => (
              <button
                key={f.facility_id}
                type="button"
                onClick={() => setSelectedFacility(f.facility_id)}
                className={`w-full flex items-center gap-3 rounded-2xl p-4 border-2 transition-all text-left ${
                  selectedFacility === f.facility_id
                    ? 'border-[#22D3EE] bg-[#ECFEFF] ring-2 ring-[#22D3EE]/30'
                    : 'border-slate-200 bg-white hover:border-slate-300'
                }`}
                data-testid={`facility-option-${f.facility_id}`}
              >
                <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                  selectedFacility === f.facility_id ? 'bg-[#22D3EE] border-[#22D3EE] text-white' : 'border-slate-300 bg-white'
                }`}>
                  {selectedFacility === f.facility_id && <CheckCircle className="w-4 h-4" />}
                </div>
                <div>
                  <span className="text-sm font-bold text-slate-700">{f.facility_name}</span>
                  <p className="text-xs text-slate-400">Public facility — admission will be recorded</p>
                </div>
              </button>
            ))}
            <button
              type="button"
              onClick={() => { setSelectedFacility('private'); setParentInWater(false); }}
              className={`w-full flex items-center gap-3 rounded-2xl p-4 border-2 transition-all text-left ${
                selectedFacility === 'private'
                  ? 'border-amber-400 bg-amber-50 ring-2 ring-amber-300/30'
                  : 'border-slate-200 bg-white hover:border-slate-300'
              }`}
              data-testid="facility-option-private"
            >
              <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                selectedFacility === 'private' ? 'bg-amber-500 border-amber-500 text-white' : 'border-slate-300 bg-white'
              }`}>
                {selectedFacility === 'private' && <CheckCircle className="w-4 h-4" />}
              </div>
              <div>
                <span className="text-sm font-bold text-slate-700">Private Pool / Other Location</span>
                <p className="text-xs text-slate-400">No facility admission will be recorded</p>
              </div>
            </button>
            {facilities.length === 0 && (
              <p className="text-xs text-slate-400 text-center py-2">No facilities set up yet. Ask your admin to invite a facility.</p>
            )}
          </div>

          {/* Parent in Water — only show when a real facility is selected */}
          {selectedFacility && selectedFacility !== 'private' && (
            <button
              type="button"
              onClick={() => setParentInWater(prev => !prev)}
              className={`w-full flex items-center gap-3 rounded-2xl p-4 border-2 transition-all text-left ${
                parentInWater
                  ? 'border-[#22D3EE] bg-[#ECFEFF] ring-2 ring-[#22D3EE]/30'
                  : 'border-slate-200 bg-white hover:border-slate-300'
              }`}
              data-testid="parent-in-water-toggle"
            >
              <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                parentInWater ? 'bg-[#22D3EE] border-[#22D3EE] text-white' : 'border-slate-300 bg-white'
              }`}>
                {parentInWater && <CheckCircle className="w-4 h-4" />}
              </div>
              <div>
                <span className="text-sm font-bold text-slate-700">Parent was present in the water</span>
                <p className="text-xs text-slate-400 mt-0.5">Adds a 3rd admission entry (instructor + child + parent)</p>
              </div>
            </button>
          )}
        </CardContent>
      </Card>

      {/* Submit */}
      {(!allCriteriaRated && criteria.length > 0) || !criticalThinkingAddressed ? (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-4 text-center" data-testid="assessment-incomplete-warning">
          <p className="text-amber-700 font-semibold">
            {!allCriteriaRated && criteria.length > 0
              ? 'Please rate all co-created criteria above before submitting'
              : 'Please complete the Critical Thinking section before submitting'}
          </p>
        </div>
      ) : null}

      {!selectedFacility && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-4 mb-4 text-center" data-testid="facility-required-warning">
          <p className="text-red-700 font-semibold text-sm">
            Please select a facility or "Private Pool" in the Session Location section before submitting
          </p>
        </div>
      )}

      <Button
        data-testid="submit-assessment-btn"
        onClick={handleSubmit}
        disabled={saving || !selectedFacility}
        className="w-full h-14 rounded-full bg-[#1E1B4B] hover:bg-[#2D2B66] text-white font-bold text-lg shadow-lg mb-12 gap-2 disabled:opacity-50"
      >
        <Save className="w-5 h-5" /> {saving ? 'Saving...' : 'Submit Assessment'}
      </Button>
    </div>
  );
}
